import { FileText, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useGetJobById } from "@elation/api-client/src/stitch-web";

export interface GapListUploadV2 {
  id: string;
  fileName: string;
  patientId: number;
  encounterId?: number;
  jobId?: string;
  failed?: boolean;
  statusText?: string;
}

export interface GapListUploadHandOffContext {
  jobId: string;
  patientId: number;
  encounterId?: number;
}

export interface GapListUploadRowV2Props {
  upload: GapListUploadV2;
  onComplete?: (context: GapListUploadHandOffContext) => void | Promise<void>;
  onRemove?: (uploadId: string) => void;
}

interface JobMetadata {
  step?: "extracting" | "persisting";
  extractedCount?: number;
  opportunitiesCreated?: number;
}

function deriveStatusText(
  status: string | undefined,
  isJobFailed: boolean,
  fallbackStatusText?: string,
  metadata?: JobMetadata,
): string {
  if (isJobFailed) return "Processing failed";
  if (status === "completed") {
    const count = metadata?.opportunitiesCreated;
    return count != null
      ? `Complete — ${count} opportunities found`
      : "Complete";
  }
  if (status === "running") {
    if (metadata?.step === "persisting") {
      const count = metadata?.extractedCount;
      return count != null
        ? `Saving ${count} findings...`
        : "Saving findings...";
    }
    return "Extracting care gaps...";
  }
  return fallbackStatusText ?? "Uploading...";
}

const REMOVE_DELAY_MS = 3000;
const HAND_OFF_RETRY_DELAY_MS = 2000;
const MAX_HAND_OFF_ATTEMPTS = 3;

export function GapListUploadRowV2({
  upload,
  onComplete,
  onRemove,
}: GapListUploadRowV2Props) {
  const { data, isError } = useGetJobById(upload.jobId ?? "", {
    query: {
      enabled: upload.jobId != null && !upload.failed,
      refetchInterval: (query) => {
        const status = query?.state?.data?.data?.status;
        if (status === "completed" || status === "failed") return false;
        return 2000;
      },
      refetchOnMount: (query) => {
        const status = query.state.data?.data?.status;
        if (status === "completed" || status === "failed") return false;
        return true;
      },
    },
  });

  const status = data?.data?.status;
  const isJobFailed = upload.failed || status === "failed";
  const isCompleted = status === "completed";
  const statusText = deriveStatusText(
    status,
    isJobFailed,
    upload.statusText,
    data?.data?.metadata as JobMetadata | undefined,
  );
  const onCompleteRef = useRef(onComplete);
  const onRemoveRef = useRef(onRemove);
  const hasHandledCompleteRef = useRef(false);
  const hasHandledFailureRef = useRef(false);
  const [handOffAttempt, setHandOffAttempt] = useState(0);
  const [handOffSucceeded, setHandOffSucceeded] = useState(false);
  const [handOffExhausted, setHandOffExhausted] = useState(false);

  onCompleteRef.current = onComplete;
  onRemoveRef.current = onRemove;

  const isPendingHandOff =
    isCompleted && !handOffSucceeded && !handOffExhausted;
  const displayStatusText = handOffExhausted
    ? "Unable to update agenda"
    : isPendingHandOff
      ? handOffAttempt > 0
        ? "Retrying agenda update..."
        : "Updating agenda..."
      : isError && !isJobFailed && !isCompleted
        ? "Checking status..."
        : statusText;
  const showSpinner =
    !isJobFailed &&
    !handOffExhausted &&
    (!isCompleted || isPendingHandOff || isError);

  useEffect(() => {
    if (!isCompleted || upload.jobId == null || hasHandledCompleteRef.current) {
      return;
    }

    hasHandledCompleteRef.current = true;
    let removeTimer: ReturnType<typeof setTimeout> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    void Promise.resolve(
      onCompleteRef.current?.({
        jobId: upload.jobId,
        patientId: upload.patientId,
        encounterId: upload.encounterId,
      }),
    )
      .then(() => {
        if (cancelled) return;
        setHandOffSucceeded(true);
        removeTimer = setTimeout(
          () => onRemoveRef.current?.(upload.id),
          REMOVE_DELAY_MS,
        );
      })
      .catch((err) => {
        console.error("[GapListUploadRowV2] Gap list hand-off failed", {
          jobId: upload.jobId,
          err,
        });
        hasHandledCompleteRef.current = false;
        if (cancelled) return;

        if (handOffAttempt + 1 >= MAX_HAND_OFF_ATTEMPTS) {
          setHandOffExhausted(true);
          removeTimer = setTimeout(
            () => onRemoveRef.current?.(upload.id),
            REMOVE_DELAY_MS,
          );
          return;
        }

        retryTimer = setTimeout(
          () => setHandOffAttempt((attempt) => attempt + 1),
          HAND_OFF_RETRY_DELAY_MS,
        );
      });

    return () => {
      cancelled = true;
      hasHandledCompleteRef.current = false;
      if (removeTimer) clearTimeout(removeTimer);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [isCompleted, upload.id, upload.jobId, handOffAttempt]);

  useEffect(() => {
    if (!isJobFailed || hasHandledFailureRef.current) return;

    hasHandledFailureRef.current = true;
    const removeTimer = setTimeout(
      () => onRemoveRef.current?.(upload.id),
      REMOVE_DELAY_MS,
    );
    return () => clearTimeout(removeTimer);
  }, [isJobFailed, upload.id]);

  return (
    <li
      aria-label={`${upload.fileName} — ${displayStatusText}`}
      className="flex items-start gap-2.5 border-b border-border py-2.5"
    >
      <span className="mt-0.5 inline-flex size-[22px] shrink-0 items-center justify-center rounded-[6px] bg-primary/10 text-primary">
        <FileText className="size-3.5" aria-hidden="true" />
      </span>
      <div className="min-w-0 leading-tight">
        <p className="truncate font-mono text-[12px] font-medium text-foreground">
          {upload.fileName}
        </p>
        <p
          className="mt-1 inline-flex items-center gap-1.5 font-mono text-[10.5px] font-semibold text-primary"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {showSpinner ? (
            <Loader2 className="size-3 animate-spin" aria-hidden="true" />
          ) : null}
          {displayStatusText}
        </p>
      </div>
    </li>
  );
}
